/**
 * Arabic-Indic digits typed into a number field, read as the Latin digits the
 * server, the receipts and every figure on the board use (rulings L4,
 * 2026-10-21: the apps and the website adopt this together).
 *
 * An Arabic or Kurdish phone keyboard types ٠–٩ (U+0660–0669), and a Persian
 * layout — which many Sorani keyboards share — types ۰–۹ (U+06F0–06F9). The
 * floor's number fields dropped them without a word: a `type="number"` field
 * sanitises such text to "" before any handler sees it, unless the browser
 * reads the page language's own digits there (inferred from the HTML
 * value-sanitisation rule), and where the text did arrive, `Number()` answers
 * NaN for every one. So "٦٠" in Duration started an open-ended sitting, and
 * "٣" in a pool table's Rounds billed it nothing (`Number('٣') || 0`). Those
 * fields are now text fields with `inputMode="numeric"` that pass through
 * here, and so are the searches that take a number: a phone, a receipt, a
 * barcode.
 *
 * Only digits move, plus the Arabic decimal separator U+066B, which becomes
 * ".". Everything else — Latin digits, letters, the Arabic thousands mark
 * U+066C, spaces — is left exactly as typed, so a barcode or a customer's
 * name with digits in it survives.
 *
 * The apps do the same with `GcDigits.normalise` (I1, A1), on the same fields
 * (I7, A7, I8, A8, I9, A9), and all three are held to `digits.vectors.json`
 * (H4g): this file's agreement test is `lib/__tests__/digits.vectors.test.ts`.
 */
/** ٠–٩, ۰–۹ and ٫, written as escapes so no editor reorders them right to left. */
const ARABIC_DIGITS = /[\u0660-\u0669\u06F0-\u06F9\u066B]/g;

export function normaliseDigits(text: string): string {
  return text.replace(ARABIC_DIGITS, (char) => {
    const code = char.charCodeAt(0);
    if (code === 0x066b) return '.';
    // Both runs are ten consecutive code points starting at their zero.
    return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660);
  });
}

/**
 * The `onChange` of a floor field that normalises its digits as they are
 * typed: `onChange={onDigitsChange(setRounds)}`.
 *
 * Storing `normaliseDigits(value)` alone moved the caret. React writes the
 * converted text back into the box after the event, and a browser puts the
 * caret at the end whenever a script writes a different value (HTML's value
 * setter), so an Arabic-Indic digit typed into the middle of a phone or a
 * receipt number sent every later key to the end (W2 review, round 2). This
 * writes the converted text itself, at once, and puts the caret back where it
 * was: every converted character is still exactly one character, so the same
 * offsets point at the same place. The box then already holds what the state
 * will hold, and React has nothing to write. A box with no caret to keep
 * (`selectionStart` null) is only converted.
 */
export function onDigitsChange(
  set: (text: string) => void,
): (event: { target: HTMLInputElement }) => void {
  return (event) => {
    const field = event.target;
    const typed = field.value;
    const text = normaliseDigits(typed);
    if (text !== typed) {
      const start = field.selectionStart;
      const end = field.selectionEnd;
      const direction = field.selectionDirection ?? undefined;
      field.value = text;
      if (typeof start === 'number' && typeof end === 'number') {
        field.setSelectionRange(start, end, direction);
      }
    }
    set(text);
  };
}
